// CPR typed on patient create/edit (Spor A C22). Shared by the web forms and the
// create-patient / update-patient Edge Functions. No imports, so Deno can load
// it directly. Values are never logged.

const DASH = /^(\d{6})-?([0-9A-Za-z]{4})$/;

/**
 * Century from the 7th digit (CPR-kontoret): 0–3 → 1900s; 4 and 9 → 2000s up
 * to yy 36, else 1900s; 5–8 → 2000s up to yy 57, else 1800s. A replacement CPR
 * (letter in the 7th place) has no century rule, so a leap year is assumed.
 */
function fullYear(yy: number, seventh: string): number | null {
  if (!/\d/.test(seventh)) return null;
  const d = Number(seventh);
  if (d <= 3) return 1900 + yy;
  if (d === 4 || d === 9) return yy <= 36 ? 2000 + yy : 1900 + yy;
  return yy <= 57 ? 2000 + yy : 1800 + yy;
}

function daysInMonth(month: number, year: number | null): number {
  if (month === 2) {
    if (year === null) return 29;
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/**
 * Ten characters, an optional dash after the sixth, and a valid date part
 * (ddmmyy). The last four are digits, or letters for an erstatningspersonnummer.
 * Modulus 11 is NOT checked: MedCom test CPRs such as 150282-9995 fail it.
 */
export function isValidCprEntry(value: string): boolean {
  const match = DASH.exec(value.trim());
  if (!match) return false;
  const datePart = match[1];
  const serial = match[2];
  const day = Number(datePart.slice(0, 2));
  const month = Number(datePart.slice(2, 4));
  const yy = Number(datePart.slice(4, 6));
  if (month < 1 || month > 12 || day < 1) return false;
  return day <= daysInMonth(month, fullYear(yy, serial[0]));
}

export type CprEntryMode = 'create' | 'update';

/**
 * Who may type a CPR: the CPR permission for the action, or the patient
 * permission for the same action (a member who may create or edit the patient
 * may enter its CPR). Seeing a CPR stays on patients_cpr:view (decrypt-cpr).
 */
export function canEnterPatientCpr(
  has: (resource: 'patients' | 'patients_cpr', action: CprEntryMode) => boolean,
  mode: CprEntryMode,
): boolean {
  return has('patients_cpr', mode) || has('patients', mode);
}

export type CprAuditChange = 'set' | 'changed' | 'cleared';

/** Audit wording for a CPR write. Only the kind of change, never the value. */
export function cprAuditChange(previouslyHadCpr: boolean, next: string | null | undefined): CprAuditChange | null {
  const nextSet = typeof next === 'string' && next.trim().length > 0;
  if (nextSet) return previouslyHadCpr ? 'changed' : 'set';
  return previouslyHadCpr ? 'cleared' : null;
}
